import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { CheckCircle2, XCircle, Shield, Send, ClipboardList, RefreshCcw, Info } from "lucide-react";

/**
 * Admin · Brand · Creative QA
 * Runs the pre-approval checklist for every VAULT marketing creative.
 * Blocks final approval when any check fails.
 */

const CREATIVE_TYPES = [
    "Instagram Post", "Instagram Carousel", "Instagram Story",
    "Threads Creative", "X Creative",
    "LinkedIn Creative", "LinkedIn Carousel",
    "Facebook Creative", "WhatsApp Promotional Creative",
    "Marketing Brochure", "Product Announcement",
    "Testimonial Creative", "Educational Creative",
];

const initial = {
    creative_type: "Instagram Post", platform: "Instagram",
    logo_url: "/brand/vault-logo.svg", handle_used: "@vaultbypd",
    website_used: "vaultbypd.com",
    colours_used: "#0B1E3F,#C9A96B,#F6F3EC",
    fonts_used:   "Fraunces,Inter",
    safe_area_ok: true, duplicate_handles: false,
    alternative_logo: false, obsolete_url: false, notes: "",
};

export default function CreativeQA() {
    const [form, setForm] = useState(initial);
    const [result, setResult] = useState(null);
    const [history, setHistory] = useState([]);

    const loadHistory = () => {
        api.get("/admin/brand/creative-qa/history", { params: { limit: 20 } })
           .then(r => setHistory(r.data?.reports || []))
           .catch(() => setHistory([]));
    };
    useEffect(() => { loadHistory(); }, []);

    const run = async () => {
        try {
            const payload = {
                ...form,
                colours_used: form.colours_used.split(",").map(s => s.trim()).filter(Boolean),
                fonts_used:   form.fonts_used.split(",").map(s => s.trim()).filter(Boolean),
            };
            const r = await api.post("/admin/brand/creative-qa/check", payload);
            setResult(r.data);
            if (r.data?.passed) toast.success("Creative approved");
            else toast.error(`Blocked: ${r.data?.issues?.length || 0} issue(s)`);
            loadHistory();
        } catch (e) { toast.error(e?.response?.data?.detail || "QA failed"); }
    };

    return (
        <div className="max-w-6xl mx-auto p-6 md:p-8" data-testid="creative-qa">
            <div className="mb-6">
                <div className="text-[11px] uppercase tracking-widest text-amber-800 font-semibold flex items-center gap-1.5">
                    <Shield size={12} /> Admin · Brand · Creative QA
                </div>
                <h1 className="font-display text-4xl sm:text-5xl text-stone-900 leading-none mt-2">Approve on brand.</h1>
                <p className="text-sm text-stone-500 mt-3 max-w-2xl">
                    Every VAULT-generated creative passes through this checklist before it leaves the studio.
                </p>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                <div className="bg-white border border-stone-200 rounded-2xl p-5 space-y-3" data-testid="qa-form">
                    <div className="text-[10.5px] uppercase tracking-widest text-stone-500 font-semibold">Check a creative</div>
                    <F label="Creative type">
                        <select value={form.creative_type} onChange={e => setForm({...form, creative_type: e.target.value})}
                                className="mt-1 w-full border border-stone-300 rounded-lg px-3 py-2 text-sm"
                                data-testid="qa-creative-type">
                            {CREATIVE_TYPES.map(c => <option key={c} value={c}>{c}</option>)}
                        </select>
                    </F>
                    <F label="Platform (optional)">
                        <input value={form.platform} onChange={e => setForm({...form, platform: e.target.value})}
                               className="mt-1 w-full border border-stone-300 rounded-lg px-3 py-2 text-sm"
                               data-testid="qa-platform" />
                    </F>
                    <F label="Logo URL used">
                        <input value={form.logo_url} onChange={e => setForm({...form, logo_url: e.target.value})}
                               className="mt-1 w-full border border-stone-300 rounded-lg px-3 py-2 text-sm"
                               data-testid="qa-logo" />
                    </F>
                    <F label="Handle used">
                        <input value={form.handle_used} onChange={e => setForm({...form, handle_used: e.target.value})}
                               className="mt-1 w-full border border-stone-300 rounded-lg px-3 py-2 text-sm"
                               data-testid="qa-handle" />
                    </F>
                    <F label="Website used">
                        <input value={form.website_used} onChange={e => setForm({...form, website_used: e.target.value})}
                               className="mt-1 w-full border border-stone-300 rounded-lg px-3 py-2 text-sm"
                               data-testid="qa-website" />
                    </F>
                    <F label="Colours used (comma-separated hex)">
                        <input value={form.colours_used} onChange={e => setForm({...form, colours_used: e.target.value})}
                               className="mt-1 w-full border border-stone-300 rounded-lg px-3 py-2 text-sm font-mono"
                               data-testid="qa-colours" />
                    </F>
                    <F label="Fonts used (comma-separated)">
                        <input value={form.fonts_used} onChange={e => setForm({...form, fonts_used: e.target.value})}
                               className="mt-1 w-full border border-stone-300 rounded-lg px-3 py-2 text-sm"
                               data-testid="qa-fonts" />
                    </F>
                    <div className="grid grid-cols-2 gap-2 pt-2 text-[12.5px]">
                        <Chk label="Safe-area compliant"    v={form.safe_area_ok}     onChange={v => setForm({...form, safe_area_ok: v})}     tid="qa-safe-area" />
                        <Chk label="Duplicate handles"      v={form.duplicate_handles} onChange={v => setForm({...form, duplicate_handles: v})} tid="qa-duplicate" invert />
                        <Chk label="Alternative logo"       v={form.alternative_logo}  onChange={v => setForm({...form, alternative_logo: v})}  tid="qa-alt-logo" invert />
                        <Chk label="Obsolete URL"           v={form.obsolete_url}      onChange={v => setForm({...form, obsolete_url: v})}      tid="qa-obsolete" invert />
                    </div>
                    <button onClick={run}
                            className="mt-3 inline-flex items-center gap-1.5 px-4 py-2 rounded-full bg-stone-900 text-white text-[13px] font-semibold hover:bg-stone-700"
                            data-testid="qa-run">
                        <Send size={12} /> Run Brand QA
                    </button>
                </div>

                <div className="space-y-4">
                    {result && (
                        <div className={`rounded-2xl border p-5 ${result.passed ? "border-emerald-200 bg-emerald-50/60" : "border-rose-200 bg-rose-50/60"}`}
                             data-testid="qa-result">
                            <div className="flex items-center gap-2 mb-2">
                                {result.passed
                                    ? <CheckCircle2 size={16} className="text-emerald-700" />
                                    : <XCircle size={16} className="text-rose-700" />}
                                <div className="text-[13.5px] font-bold text-stone-900">
                                    {result.approval_status}
                                    <span className="ml-2 text-[10.5px] font-mono text-stone-500">{result.creative_type}</span>
                                </div>
                            </div>
                            {result.block_reason && (
                                <div className="text-[12px] text-rose-800 mb-2">{result.block_reason}</div>
                            )}
                            {(result.issues || []).map(iss => (
                                <div key={iss.code} className="text-[12px] text-rose-800 flex items-start gap-2">
                                    <XCircle size={11} className="mt-0.5 shrink-0" />
                                    <span><b>{iss.code}:</b> {iss.message}</span>
                                </div>
                            ))}
                            {result.passed && (
                                <div className="text-[12px] text-emerald-800">All brand checks passed.</div>
                            )}
                            <div className="mt-4 pt-3 border-t border-stone-100/80">
                                <div className="text-[10.5px] uppercase tracking-widest text-stone-500 font-semibold">Suggested footer</div>
                                <div className="text-[12.5px] text-stone-800 mt-1 font-mono">{result.footer_reference?.compact}</div>
                            </div>
                        </div>
                    )}
                    <div className="bg-white border border-stone-200 rounded-2xl overflow-hidden" data-testid="qa-history">
                        <div className="px-4 py-3 border-b border-stone-100 flex items-center justify-between">
                            <div className="flex items-center gap-2">
                                <ClipboardList size={13} className="text-stone-500" />
                                <h2 className="text-lg font-display text-stone-900">Recent QA reports</h2>
                            </div>
                            <button onClick={loadHistory} className="text-[11.5px] font-semibold text-stone-500 hover:text-stone-900 inline-flex items-center gap-1"
                                    data-testid="qa-refresh"><RefreshCcw size={11} /> Refresh</button>
                        </div>
                        {history.length === 0 ? (
                            <div className="p-8 text-center text-sm text-stone-400">No reports yet.</div>
                        ) : history.map(r => (
                            <div key={r.id} className="border-b border-stone-100 last:border-0 px-4 py-2.5 flex items-center justify-between gap-2"
                                 data-testid={`qa-report-${r.id}`}>
                                <div className="min-w-0">
                                    <div className="text-[13px] font-semibold text-stone-900 flex items-center gap-1.5">
                                        {r.passed ? <CheckCircle2 size={12} className="text-emerald-700" /> : <XCircle size={12} className="text-rose-700" />}
                                        {r.creative_type}
                                        {!r.passed && <span className="text-[10.5px] text-rose-800 ml-1">· {(r.issues || []).length} issue(s)</span>}
                                    </div>
                                    <div className="text-[10.5px] text-stone-500">{r.created_at?.slice(0, 16).replace("T", " ")} · by {r.checked_by || "—"}</div>
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            </div>

            <div className="mt-6 flex items-start gap-2 text-[11px] text-stone-500 max-w-3xl">
                <Info size={12} className="mt-0.5 shrink-0" />
                Every VAULT creative must use one approved logo, the universal <b>@vaultbypd</b> handle
                (LinkedIn may also use <b>VAULT by PD</b>), <b>vaultbypd.com</b> as the sole website, and
                approved colours + typography. Handles are stated once — do not repeat &ldquo;Instagram @vaultbypd
                / Threads @vaultbypd / X @vaultbypd.&rdquo;
            </div>
        </div>
    );
}

function F({ label, children }) {
    return (<label className="block text-[11px] uppercase tracking-widest text-stone-500 font-semibold">{label}{children}</label>);
}

function Chk({ label, v, onChange, tid, invert }) {
    // For an inverted checkbox the UI shows "good state when v is false".
    // The DOM `checked` therefore reflects the *negation* of the stored
    // value; the write path negates again so the two stay in sync.
    const displayed = invert ? !v : !!v;
    return (
        <label className="inline-flex items-center gap-2 border border-stone-200 rounded-lg px-2.5 py-1.5 cursor-pointer">
            <input type="checkbox" checked={displayed}
                   onChange={e => onChange(invert ? !e.target.checked : e.target.checked)}
                   data-testid={tid} />
            <span className="text-stone-800">{label}</span>
        </label>
    );
}
